import { useState, useRef, useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://greenlyelectronicsrecycling.com';
// const FORM_URL = 'https://readdy.ai/api/form/d76viciqo7glkto3dut0';
const FORM_URL = 'https://greenlyelectronicsrecycling.com/api/resend-readyai-form.php' ;


interface FormValues {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  zipcode: string;
  number_of_items: string;
  items_description: string;
  website_url?: string;
}

interface FormErrors {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  zipcode?: string;
  number_of_items?: string;
  items_description?: string;
}

interface TouchedFields {
  first_name?: boolean;
  last_name?: boolean;
  email?: boolean;
  phone?: boolean;
  zipcode?: boolean;
  number_of_items?: boolean;
  items_description?: boolean;
  website_url?: boolean;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+\d][\d\s\-().]{6,19}$/;
const ZIP_RE = /^\d{5}(-\d{4})?$/;

const validate = (values: FormValues): FormErrors => {
  const errors: FormErrors = {};

  if (!values.first_name.trim()) {
    errors.first_name = 'First name is required.';
  } else if (values.first_name.trim().length < 2) {
    errors.first_name = 'First name must be at least 2 characters.';
  }

  if (!values.last_name.trim()) {
    errors.last_name = 'Last name is required.';
  } else if (values.last_name.trim().length < 2) {
    errors.last_name = 'Last name must be at least 2 characters.';
  }

  if (!values.email.trim()) {
    errors.email = 'Email address is required.';
  } else if (!EMAIL_RE.test(values.email.trim())) {
    errors.email = 'Please enter a valid email address.';
  }

  if (!values.phone.trim()) {
    errors.phone = 'Phone number is required.';
  } else if (!PHONE_RE.test(values.phone.trim())) {
    errors.phone = 'Please enter a valid phone number.';
  }

  if (!values.zipcode.trim()) {
    errors.zipcode = 'Zipcode is required.';
  } else if (!ZIP_RE.test(values.zipcode.trim())) {
    errors.zipcode = 'Please enter a valid 5-digit US zipcode.';
  }

  if (!values.number_of_items.trim()) {
    errors.number_of_items = 'Number of items is required.';
  } else if (parseInt(values.number_of_items, 10) < 1) {
    errors.number_of_items = 'Must be at least 1 item.';
  }

  if (!values.items_description.trim()) {
    errors.items_description = 'Please describe the items you are dropping off.';
  } else if (values.items_description.trim().length < 10) {
    errors.items_description = 'Description must be at least 10 characters.';
  }

  return errors;
};

const INITIAL_VALUES: FormValues = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  zipcode: '',
  number_of_items: '',
  items_description: '',
  website_url: '',
};

const ErrorMsg = ({ msg }: { msg?: string }) =>
  msg ? (
    <p className="flex items-center gap-1 text-sm text-red-600 mt-1">
      <i className="ri-error-warning-line shrink-0"></i>
      {msg}
    </p>
  ) : null;

const fieldClass = (touched: boolean | undefined, error: string | undefined) =>
  `w-full border rounded-md px-3 py-2.5 text-base text-gray-800 placeholder-gray-400 focus:outline-none transition-colors ${
    touched && error
      ? 'border-red-400 bg-red-50 focus:border-red-500'
      : 'border-gray-300 focus:border-[#1B6B3A]'
  }`;

const DropOffFormPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');
  const [values, setValues] = useState<FormValues>(INITIAL_VALUES);
  const [touched, setTouched] = useState<TouchedFields>({});
  const formRef = useRef<HTMLFormElement>(null);

  const errors = validate(values);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setValues((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setServerError('');

    // Mark all fields as touched to show all errors
    const allTouched: TouchedFields = {
      first_name: true,
      last_name: true,
      email: true,
      phone: true,
      zipcode: true,
      number_of_items: true,
      items_description: true,
    };
    setTouched(allTouched);

    const currentErrors = validate(values);
    if (Object.keys(currentErrors).length > 0) return;

    // Honeypot check — bots fill this, real users don't see it
    if (values.website_url && values.website_url.trim() !== '') {
      setSubmitted(true);
      setValues(INITIAL_VALUES);
      setTouched({});
      return;
    }

    setSubmitting(true);

    const params = new URLSearchParams();
    (Object.keys(values) as (keyof FormValues)[]).forEach((key) => {
      if (key !== 'website_url') params.append(key, values[key] as string);
    });
    params.append('page', 'dropoff-form');

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        setValues(INITIAL_VALUES);
        setTouched({});
        formRef.current?.reset();
      } else {
        setServerError('Something went wrong. Please try again.');
      }
    } catch {
      setServerError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F9F7] flex flex-col">
      <title>E-Waste Drop Off Form | Schedule Electronics Drop-Off | Greenly Los Angeles</title>
      <meta name="description" content="Schedule your free e-waste drop-off at Greenly Electronics Recycling in Los Angeles. Quick online form — we'll be ready when you arrive. Two LA locations. No appointment needed." />
      <meta name="keywords" content="e-waste drop off Los Angeles, electronics drop off LA, Greenly drop off form" />
      <meta name="robots" content="index, follow" />
      <meta name="last-modified" content="2026-05-11" />
      <link rel="canonical" href={`${SITE_URL}/dropoff-form`} />
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "WebPage",
            "@id": `${SITE_URL}/dropoff-form#webpage`,
            "url": `${SITE_URL}/dropoff-form`,
            "name": "E-Waste Drop Off Form | Schedule Electronics Drop-Off | Greenly Los Angeles",
            "description": "Schedule your e-waste drop-off at Greenly Electronics Recycling in Los Angeles. Fill out our quick form and we'll be ready when you arrive. Two LA locations.",
            "isPartOf": { "@id": `${SITE_URL}/#website` },
            "about": { "@id": `${SITE_URL}/#business` },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
                { "@type": "ListItem", "position": 2, "name": "Drop-Off Form", "item": `${SITE_URL}/dropoff-form` }
              ]
            }
          },
          {
            "@type": "Service",
            "@id": `${SITE_URL}/dropoff-form#service`,
            "name": "E-Waste Drop-Off — Los Angeles",
            "description": "Free electronics drop-off at two Los Angeles locations. Schedule your drop-off online and we'll be ready when you arrive. Accepts TVs, computers, batteries, phones, and all electronics.",
            "url": `${SITE_URL}/dropoff-form`,
            "provider": { "@id": `${SITE_URL}/#business` },
            "areaServed": { "@type": "City", "name": "Los Angeles" },
            "serviceType": "Electronics Recycling Drop-Off",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
              "description": "Free electronics drop-off at two Los Angeles locations"
            }
          }
        ]
      })}</script>
      <Navbar />

      <main className="flex-1 pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-4 md:px-6">

          {/* Page Title */}
          <div className="text-center mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              E-Waste Drop Off Form
            </h1>
            <p className="text-base text-gray-500">
              Schedule your drop-off and we&apos;ll be ready when you arrive.
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 md:p-8">

            {submitted ? (
              <div className="py-4">
                {/* Header */}
                <div className="flex flex-col items-center text-center mb-7">
                  <div className="w-16 h-16 flex items-center justify-center bg-[#1B6B3A] rounded-full mb-4">
                    <i className="ri-checkbox-circle-line text-white text-3xl"></i>
                  </div>
                  <h2 className="text-xl font-extrabold text-gray-900 mb-2">Drop-Off Request Submitted!</h2>
                  <p className="text-base text-gray-500 max-w-sm">
                    We&apos;ve got your info. Here&apos;s what to expect before you arrive.
                  </p>
                </div>

                {/* Steps */}
                <div className="space-y-3 mb-7">
                  {[
                    { icon: 'ri-mail-check-line', title: 'Confirmation email sent', desc: 'A copy of your drop-off request is heading to your inbox now.' },
                    { icon: 'ri-team-line', title: "We'll be ready for you", desc: 'Our team will have your request on file so check-in is quick and easy.' },
                    { icon: 'ri-file-list-3-line', title: 'Bring a valid ID', desc: "For TVs and monitors, CalRecycle requires your name, address, and phone from your driver's license." },
                  ].map((step) => (
                    <div key={step.title} className="flex items-start gap-4 p-4 bg-[#F4FAF7] rounded-xl">
                      <div className="w-9 h-9 flex items-center justify-center bg-[#1B6B3A] rounded-full shrink-0">
                        <i className={`${step.icon} text-white text-base`}></i>
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 text-base">{step.title}</p>
                        <p className="text-sm text-gray-500 mt-0.5 leading-relaxed">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Locations */}
                <div className="mb-5">
                  <p className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">Drop-off locations</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { name: 'Downtown/ K-Town', addr: '2542 W. 3rd Street, Los Angeles, CA 90057', hours: 'Mon - Sun: 10:00 AM - 6:00 PM' },
                      { name: 'South LA', addr: '4902 W. Slauson Ave, Los Angeles, CA 90056', hours: 'Mon - Fri: 10:00 AM - 4:00 PM' },
                    ].map((loc) => (
                      <div key={loc.name} className="border border-gray-200 rounded-xl p-4">
                        <p className="font-bold text-gray-900 text-sm mb-1">{loc.name}</p>
                        <p className="text-sm text-gray-500 flex items-start gap-1.5 mb-1">
                          <i className="ri-map-pin-line text-[#1B6B3A] shrink-0 mt-0.5"></i>{loc.addr}
                        </p>
                        <p className="text-sm text-gray-400 flex items-center gap-1.5">
                          <i className="ri-time-line text-[#1B6B3A] shrink-0"></i>{loc.hours}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Call CTA */}
                <div className="bg-[#1B6B3A] rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-3 mb-5">
                  <p className="text-white text-sm font-semibold">Questions before you come in?</p>
                  <a
                    href="tel:+12136001000"
                    className="inline-flex items-center gap-2 bg-white text-[#1B6B3A] font-bold text-sm px-5 py-2 rounded-lg hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-phone-fill"></i> (213) 600-1000
                  </a>
                </div>

                <button
                  onClick={() => setSubmitted(false)}
                  className="w-full border border-gray-300 text-gray-600 text-base font-semibold py-2.5 rounded-md hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Submit Another Request
                </button>
              </div>
            ) : (
              <form
                ref={formRef}
                onSubmit={handleSubmit}
                noValidate
                className="space-y-5"
              >
                <input type="hidden" name="page" value="dropoff-form" />
                {/* Honeypot field — hidden from real users, bots will fill it */}
                <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0, pointerEvents: 'none', tabIndex: -1 }} aria-hidden="true">
                  <label htmlFor="website_url_hp_dropoff">Website URL</label>
                  <input
                    id="website_url_hp_dropoff"
                    type="text"
                    name="website_url"
                    value={values.website_url}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>
                {/* First + Last Name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-base text-gray-700 mb-1.5">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      value={values.first_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="John"
                      className={fieldClass(touched.first_name, errors.first_name)}
                    />
                    <ErrorMsg msg={touched.first_name ? errors.first_name : undefined} />
                  </div>
                  <div>
                    <label className="block text-base text-gray-700 mb-1.5">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      value={values.last_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Doe"
                      className={fieldClass(touched.last_name, errors.last_name)}
                    />
                    <ErrorMsg msg={touched.last_name ? errors.last_name : undefined} />
                  </div>
                </div>

                {/* Email + Phone */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-base text-gray-700 mb-1.5">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="john@gmail.com"
                      className={fieldClass(touched.email, errors.email)}
                    />
                    <ErrorMsg msg={touched.email ? errors.email : undefined} />
                  </div>
                  <div>
                    <label className="block text-base text-gray-700 mb-1.5">
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={values.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="(213) 000-0000"
                      className={fieldClass(touched.phone, errors.phone)}
                    />
                    <ErrorMsg msg={touched.phone ? errors.phone : undefined} />
                  </div>
                </div>

                {/* Zipcode */}
                <div>
                  <label className="block text-base text-gray-700 mb-1.5">
                    Zipcode <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="zipcode"
                    value={values.zipcode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="90057"
                    maxLength={10}
                    className={fieldClass(touched.zipcode, errors.zipcode)}
                  />
                  <ErrorMsg msg={touched.zipcode ? errors.zipcode : undefined} />
                </div>

                {/* Number of Items */}
                <div>
                  <label className="block text-base text-gray-700 mb-1.5">
                    Number of Items <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="number_of_items"
                    value={values.number_of_items}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    min={1}
                    placeholder="e.g. 5"
                    className={fieldClass(touched.number_of_items, errors.number_of_items)}
                  />
                  <ErrorMsg msg={touched.number_of_items ? errors.number_of_items : undefined} />
                </div>

                {/* Brief Items Description */}
                <div>
                  <label className="block text-base text-gray-700 mb-1.5">
                    Brief Items Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="items_description"
                    value={values.items_description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    rows={4}
                    maxLength={500}
                    placeholder="e.g. 2 laptops, 1 desktop, 3 monitors..."
                    className={`${fieldClass(touched.items_description, errors.items_description)} resize-none`}
                  />
                  <div className="flex items-center justify-between mt-0.5">
                    <ErrorMsg msg={touched.items_description ? errors.items_description : undefined} />
                    <p className={`text-sm ml-auto ${values.items_description.length >= 500 ? 'text-red-500' : 'text-gray-400'}`}>
                      {values.items_description.length}/500
                    </p>
                  </div>
                </div>

                {/* Server Error */}
                {serverError && (
                  <p className="flex items-center gap-2 text-base text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                    <i className="ri-error-warning-line shrink-0"></i>
                    {serverError}
                  </p>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#1B6B3A] hover:bg-[#155530] disabled:opacity-60 text-white text-base font-bold py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      Submit Request
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Info strip */}
          <div className="mt-5 bg-[#FFF3CD] border border-[#FBBF24] rounded-xl px-5 py-4">
            <p className="text-sm font-bold text-[#92400E] mb-1">Important Note:</p>
            <p className="text-sm text-[#78350F] leading-relaxed">
              All TVs and Monitor Screens drop-off must have been used in California and require basic personal information to be collected as required by CalRecycle. Please be ready to provide your Name, Address and Phone number as they appear on your driver&apos;s license.
            </p>
          </div>

          {/* Locations reminder */}
          <div className="mt-4 flex flex-col sm:flex-row gap-4">
            {[
              { name: 'Downtown/ K-Town', addr: '2542 W. 3rd Street, Los Angeles, CA 90057', hours: 'Mon - Sun: 10:00 AM - 6:00 PM' },
              { name: 'South LA', addr: '4902 W. Slauson Ave, Los Angeles, CA 90056', hours: 'Mon - Fri: 10:00 AM - 4:00 PM' }, 
            ].map((loc) => (
              <div key={loc.name} className="flex-1 bg-white border border-gray-200 rounded-xl px-5 py-4">
                <p className="text-base font-semibold text-gray-800 mb-1">{loc.name}</p>
                <p className="text-sm text-gray-500 mb-1 flex items-start gap-1.5">
                  <i className="ri-map-pin-line text-[#1B6B3A] mt-0.5 shrink-0"></i>
                  {loc.addr}
                </p>
                <p className="text-sm text-gray-400 flex items-center gap-1.5">
                  <i className="ri-time-line text-[#1B6B3A] shrink-0"></i>
                  {loc.hours}
                </p>
              </div>
            ))}
          </div>

        </div>
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default DropOffFormPage;
